import { UseAppFormProps } from "hooks/useAppForm";
import { Component, FC, Fragment, ReactNode, useMemo } from "react";
import Select from "./Select";
import { FormCodeEditor } from "./CodeEditor";
import Password from "./Password";
import Input from "./Input";
import Number from "./Number";
import Color from "./Color";
import Textarea from "./Textarea";
import DatePicker from "./DatePicker";
import Checkbox from "./Checkbox";
import Radio from "./Radio";
import UploadImage from "./UploadImage";
import Editor from "./Editor";


interface AppFormProps<T> {
    id?: string;
    form: UseAppFormProps<T>
    onSubmit: (values: T) => void;
    children?: ReactNode;
    cols?: 1 | 2 | 3 | 4;
    noStyle?: boolean;
    shadow?: boolean;
    className?: string;
}
const Form = <T extends object>({
    id,
    form,
    onSubmit,
    children,
    cols,
    noStyle,
    shadow = true,
    ...props
}: AppFormProps<T>) => {

    const { validateForm, values } = form;
    const className = useMemo(() => {
        let cls = noStyle ? `${props.className} ` : `bg-white p-4 rounded ${props.className} `;
        if (shadow) {
            cls += " shadow "
        }
        switch (cols) {
            case 2:
                return cls + "grid grid-cols-1 md:grid-cols-2 gap-x-4 w-full ";
            case 3:
                return cls + "grid grid-cols-1 md:grid-cols-3 gap-x-4 w-full ";
            case 4:
                return cls + "grid grid-cols-1 md:grid-cols-4 gap-x-4 w-full ";
            default:
                return cls + "w-full";

        }
    }, [cols, noStyle, props.className])
    return <Fragment>
        <form
            id={id}
            ref={form.formRef}
            className={className}
            onSubmit={(e) => {
                e.preventDefault();
                if (validateForm()) {
                    onSubmit(values);
                }
            }}
        >
            {children}
        </form>
    </Fragment>
}
interface ActionProps {
    col?: number;
    children?: ReactNode;
}
const Action: FC<ActionProps> = ({ col, children }) => {
    const className = useMemo(() => {
        if (col) {
            return `flex justify-end space-x-2 md:col-span-${col} mt-2`;
        }
        return "flex justify-end space-x-2 ";
    }, [col])
    return <div className={className}>
        {children}
    </div>
}

export interface ItemProps {
    col?: number;
    label?: ReactNode;
    required?: boolean;
    children?: ReactNode;
    error?: string;
    className?: string;
    itemClassName?: string;
    readOnly?: boolean;
    helper?: ReactNode;
}
export const Item: FC<ItemProps> = ({ col, label, required, children, error, itemClassName, helper, ...props }) => {
    const className = useMemo(() => {
        if (col) {
            return `relative mb-2 md:col-span-${col} ${itemClassName} ${props.className}`;
        }
        return `relative mb-2 ${itemClassName} ${props.className}`;
    }, [col, itemClassName, props.className])
    return <div className={className}>
        {label && <label className="block text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>}
        {children}
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        {helper}
    </div>
}

class AppForm<T extends object> extends Component<AppFormProps<T>> {
    static Item = Item;
    static Input = Input;
    static Password = Password;
    static Number = Number;
    static Color = Color;
    static Textarea = Textarea;
    static Select = Select;
    static DatePicker = DatePicker;
    static Editor = Editor;
    static Checkbox = Checkbox;
    static Radio = Radio;
    static CodeEditor = FormCodeEditor;
    static UploadImage = UploadImage;
    static Action = Action;
    render(): ReactNode {
        return <Form<T> {...this.props} />
    }
}
export default AppForm;